import { Component, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode, RefObject } from 'react'
import { getExhibit } from '../content/exhibits'
import { useVisitStore } from '../domain/store'
import { useShallow } from 'zustand/react/shallow'
import { EXHIBIT_PREVIEW_MS } from '../domain/proximity'
import type { ArchiveRecord, BehaviorAction, ChannelName, Exhibit, Region } from '../shared/types'
import { useMuseumAudio } from './useMuseumAudio'
import type { MuseumAudioController } from './useMuseumAudio'
import MuseumLoading from './MuseumLoading'
import HomeHero from './HomeHero'
import { failLoading, setLoadingPhase } from '../scene/loadingProgress'
import './styles.css'

let sceneModule: Promise<typeof import('../scene/MuseumScene')> | undefined
function loadMuseumScene() {
  if (!sceneModule) {
    setLoadingPhase('engine')
    sceneModule = import('../scene/MuseumScene')
  }
  return sceneModule
}
const MuseumScene = lazy(loadMuseumScene)

class SceneLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() { return { failed: true } }
  componentDidCatch(error: Error) { failLoading(error) }
  render() { return this.state.failed ? <MuseumLoading failed /> : this.props.children }
}

const ART_BY_REGION: Record<Region, string> = {
  1: '/assets/unity/art-07.png',
  2: '/assets/unity/art-04.png',
  3: '/assets/unity/art-17.png',
  4: '/assets/unity/art-12.png',
}
const ACTION_LABEL: Record<BehaviorAction, string> = {
  browse: '驻足观看', play: '观看影像', agree: '赞同', oppose: '反对', ignore: '忽略',
  comment: '留下留言', share: '转发', grow: '种草', report: '举报',
}
const POINT_CODE: Record<Region, string> = { 1: '01', 2: '04', 3: '02', 4: '05' }

function useStageFit(stage: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const update = () => {
      const scale = Math.min(window.innerWidth / 1600, window.innerHeight / 900)
      document.documentElement.style.setProperty('--fit-scale', String(scale))
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [stage])
}

function formatTime(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function Arrow() {
  return <span className="button-arrow" aria-hidden="true"><svg viewBox="0 0 32 22"><path d="M3 11h26M21 3l8 8-8 8" /></svg></span>
}

function Art({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  return <figure className={`unity-art ${className}`}><img src={src} alt={alt} /></figure>
}

function SoundButton({ audio }: { audio: MuseumAudioController }) {
  const label = audio.needsGesture ? '开启声音' : audio.muted ? '开启声音' : '静音'
  return <button className="sound-button" type="button" onClick={audio.toggle} aria-label={label} title={label}>{label}</button>
}

function Topbar({ onFullscreen, fullscreen, audio }: { onFullscreen: () => void; fullscreen: boolean; audio: MuseumAudioController }) {
  return <header className="topbar"><span className="c430-wordmark" aria-label="C430">C430</span><div className="topbar-actions"><SoundButton audio={audio} /><button className="fullscreen-button" type="button" onClick={onFullscreen} aria-label={fullscreen ? '退出全屏' : '全屏观看'} title={fullscreen ? '退出全屏' : '全屏观看'}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5" /></svg></button></div></header>
}

function BackButton({ onClick, label = '返回' }: { onClick: () => void; label?: string }) {
  return <button className="back-button" type="button" onClick={onClick}><svg viewBox="0 0 32 22" aria-hidden="true"><path d="M29 11H3M11 3l-8 8 8 8" /></svg><span>{label}</span></button>
}

function StatusMark({ status }: { status: string }) {
  return <span className={`status-mark status-mark--${status}`} aria-hidden="true" />
}

function AutoEnterSetting({ enabled, onChange }: { enabled: boolean; onChange: (enabled: boolean) => void }) {
  return <label className="auto-enter-setting">
    <input type="checkbox" checked={enabled} onChange={(event) => onChange(event.target.checked)} />
    <span>靠近展品自动进入<small>开启后停留 {EXHIBIT_PREVIEW_MS / 1000} 秒进入；关闭时按 E 或点击入口打开。</small></span>
  </label>
}

function ExhibitPreview({ exhibit, onCancel }: {
  exhibit: Exhibit
  onCancel: () => void
}) {
  const remainingMs = useVisitStore((state) => state.previewRemainingMs)
  const autoEnterExhibits = useVisitStore((state) => state.autoEnterExhibits)
  const countdown = remainingMs === null ? null : Math.max(1, Math.ceil(remainingMs / 1000))
  const progress = remainingMs === null ? 0 : Math.min(1, Math.max(0, 1 - remainingMs / EXHIBIT_PREVIEW_MS))
  return <section className="exhibit-preview" aria-label={`${exhibit.title}预览`}>
    <div className="exhibit-preview__top"><p>采样点 {POINT_CODE[exhibit.region]} · 展品预览</p>{countdown !== null && <button type="button" onClick={onCancel} aria-label="取消本次自动进入" title="取消自动进入">×</button>}</div>
    <div className="exhibit-preview__image"><img src={ART_BY_REGION[exhibit.region]} alt={exhibit.title} /></div>
    <h2>{exhibit.title}</h2>
    <p className="exhibit-preview__copy">{exhibit.description}</p>
    <div className="exhibit-preview__entry"><span role="status" aria-live="polite">{countdown !== null ? <><strong>{countdown}</strong> 秒后自动进入</> : '按 E 打开展品'}</span><small>{countdown !== null ? '离开可取消 · E 立即进入' : autoEnterExhibits ? '离开后再次靠近可自动进入' : '自动进入已关闭 · 也可点击入口打开'}</small></div>
    {countdown !== null && <div className="exhibit-preview__progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>}
  </section>
}

function ExhibitDialog({ exhibit, onClose, onRecord, recordedActions, audio }: {
  exhibit: Exhibit
  onClose: () => void
  onRecord: (action: BehaviorAction, detail?: string) => boolean
  recordedActions: BehaviorAction[]
  audio: MuseumAudioController
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const [comment, setComment] = useState('')
  const [feedback, setFeedback] = useState('')
  const [videoError, setVideoError] = useState('')
  const [videoReady, setVideoReady] = useState(false)
  const setVideoPlaying = audio.setVideoPlaying
  const region = exhibit.region
  const art = ART_BY_REGION[region]
  const title = region === 1 ? '影像采样' : region === 2 ? '态度采样' : region === 3 ? '留言采样' : '传播采样'

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    closeRef.current?.focus({ preventScroll: true })
    const pauseForBackground = () => videoRef.current?.pause()
    const onVisibility = () => { if (document.hidden) pauseForBackground() }
    window.addEventListener('blur', pauseForBackground)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', pauseForBackground)
      document.removeEventListener('visibilitychange', onVisibility)
      videoRef.current?.pause()
      setVideoPlaying(false)
      openerRef.current?.focus({ preventScroll: true })
    }
  }, [setVideoPlaying])

  const takeAction = useCallback((action: BehaviorAction, detail?: string) => {
    const ok = onRecord(action, detail)
    setFeedback(ok ? '已记录' : recordedActions.includes(action) ? '此处已留下选择' : '暂未记录，请在探索中重试')
    return ok
  }, [onRecord, recordedActions])

  const playVideo = async () => {
    const video = videoRef.current
    if (!video) return
    setVideoError('')
    try { await video.play() }
    catch { setVideoError('影像暂时无法播放。请使用播放器控件重试，或确认原始素材已载入。') }
  }

  const submitComment = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const text = comment.trim().slice(0, 60)
    if (!text) { setFeedback('请先写下一句话。'); return }
    if (takeAction('comment', text)) setComment('')
  }

  const onKeys = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), textarea:not(:disabled), video[controls]')
      if (focusable?.length) {
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
      }
      return
    }
    if (region !== 2 && region !== 4) return
    if (!/^[1-3]$/.test(event.key)) return
    const buttons = panelRef.current?.querySelectorAll<HTMLButtonElement>('[data-choice]')
    const selected = buttons?.[Number(event.key) - 1]
    if (selected) { event.preventDefault(); selected.click() }
  }

  const choose = (action: BehaviorAction, detail: string) => { if (takeAction(action, detail)) onClose() }
  const selectButton = (action: BehaviorAction, detail: string, index: number) => <button className="choice-row" data-choice type="button" onClick={() => choose(action, detail)} key={action}><span className="choice-number">{index + 1}</span><span className="choice-copy"><strong className="choice-label">{ACTION_LABEL[action]}</strong><small className="choice-detail">{detail}</small></span><span className="choice-arrow" aria-hidden="true">↗</span></button>

  return <div className="sample-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <div className={`sampling-visual sampling-visual--region-${region}`}><Art src={art} alt={`${title}原作`} className={`overlay-art overlay-art--region-${region}`} /></div>
    <section ref={panelRef} className={`sample-panel sample-panel--region-${region}`} role="dialog" aria-modal="true" aria-labelledby="sample-title" onKeyDown={onKeys}>
      <div className="sample-panel__top"><span className="reading-label">{title} / {POINT_CODE[region]}</span><div className="sample-panel__tools"><SoundButton audio={audio} /><button ref={closeRef} className="close-button" type="button" onClick={onClose} aria-label="关闭">×</button></div></div>
      {region === 1 && <>
        <div className="video-frame"><video ref={videoRef} src="/assets/unity/motion-17.mp4" poster={art} controls playsInline preload="metadata" muted={audio.muted} aria-label="原始影像：手掌构成的羽翼" onLoadedMetadata={() => { setVideoReady(true); setVideoError('') }} onPlay={() => { audio.setVideoPlaying(true); if (!recordedActions.includes('play')) takeAction('play', '观看影像') }} onPause={() => audio.setVideoPlaying(false)} onEnded={() => audio.setVideoPlaying(false)} onError={() => setVideoError('影像未能载入。请确认展览素材已准备，或稍后重试。')} /></div>
        <div className="film-caption"><p><span>原始影像 / 01</span><strong id="sample-title">手掌构成的羽翼</strong></p><div className="film-actions"><button className="line-action" type="button" onClick={() => void playVideo()}>播放影像 ↗</button><button className="line-action" type="button" onClick={onClose}>继续探索 →</button></div></div>
        {!videoReady && !videoError && <p className="quiet-hint">影像尚未载入；使用播放器控件开始播放。</p>}
        {videoError && <p className="sample-feedback sample-feedback--error" role="status">{videoError}</p>}
      </>}
      {region === 2 && <>
        <p className="reading-label">你的看法</p><h2 id="sample-title">公司团建，算不算上班？</h2><p className="sample-context">工作与休息，应该在哪里划线？</p>
        <div className="choice-list" aria-label="选择你的回应">{selectButton('agree', '团建也是工作', 0)}{selectButton('oppose', '休息时间应由自己安排', 1)}{selectButton('ignore', '暂不作出判断', 2)}</div><p className="quiet-hint">按 1 / 2 / 3，或选择一项</p>
      </>}
      {region === 3 && <>
        <p className="reading-label">留言采样 / 02</p><h2 id="sample-title">留下一句话。</h2><p className="sample-context">为这段人类记忆留下一句话。</p>
        <form className="message-form" onSubmit={submitComment}><label className="sr-only" htmlFor="museum-comment">你的留言</label><textarea id="museum-comment" value={comment} maxLength={60} rows={3} onChange={(event) => setComment(event.target.value)} placeholder="写下你此刻的想法" /><div className="message-meta"><span>{comment.length} / 60 字</span><button className="primary-button" type="submit" disabled={!comment.trim()}><span>留下回应</span><Arrow /></button></div></form>
      </>}
      {region === 4 && <>
        <p className="reading-label">传播采样 / 05</p><h2 id="sample-title">你会怎样分享这件作品？</h2><p className="sample-context">你的回应，也会成为一条传播线索。</p>
        <div className="choice-list" aria-label="选择一种回应">{selectButton('share', '传给别人', 0)}{selectButton('grow', '推荐给别人', 1)}{selectButton('report', '标记这条内容', 2)}</div><p className="quiet-hint">按 1 / 2 / 3，或选择一项</p>
      </>}
      <p className="sample-feedback" role="status" aria-live="polite">{feedback}</p>
      {region !== 1 && <button className="line-action sample-continue" type="button" onClick={onClose}>继续探索 <span aria-hidden="true">→</span></button>}
    </section>
  </div>
}

function ArchiveView({ archive, archives, onSelect, onRetry, onNewVisit, onEntrance, persistenceStatus, onExport, audio }: {
  archive: ArchiveRecord | null
  archives: ArchiveRecord[]
  onSelect: (id: string) => void
  onRetry: (id: string, channel: ChannelName) => void
  onNewVisit: () => void
  onEntrance: () => void
  persistenceStatus: string
  onExport: (archive: ArchiveRecord) => void
  audio: MuseumAudioController
}) {
  const [showHistory, setShowHistory] = useState(false)
  const events = archive?.snapshot.events ?? []
  return <main className="archive-stage" id="stage">
    <Art src="/assets/unity/art-14.png" alt="原作14：黑底上展开的白翼" className="archive-background" />
    <header className="topbar"><span className="c430-wordmark">C430</span><div className="topbar-actions"><span className="archive-persist"><StatusMark status={persistenceStatus} />{persistenceStatus === 'saving' ? '保存中' : persistenceStatus === 'saved' ? '本机档案' : persistenceStatus === 'error' ? '尚未保存 · 可导出' : '档案'}</span><SoundButton audio={audio} /></div></header>
    <div className="archive-scroll">
      <BackButton onClick={onEntrance} label="回到入口" />
      <section className="archive-title"><p className="reading-label">参观档案 / {archive?.snapshot.visitId.slice(0, 8) ?? '历史'}</p><h1>{archive ? `${archive.snapshot.visitorLabel} 的参观记录` : '历史档案'}</h1><p>{archive?.summary ?? '选择一份已保存在本机的参观档案。'}</p>
        <div className="archive-actions"><button className="primary-button" type="button" onClick={onNewVisit}><span>再参观一次</span><Arrow /></button><button className="plain-action" type="button" onClick={() => setShowHistory((value) => !value)}>{showHistory ? '收起历史' : '历史档案'} ↗</button>{archive && <button className="plain-action" type="button" onClick={() => onExport(archive)}>导出 JSON ↓</button>}</div>
        {persistenceStatus === 'error' && <p className="archive-error" role="alert">本机档案未保存，可导出 JSON 备份。</p>}
      </section>
      {showHistory && <section className="archive-history" aria-label="历史档案列表">{archives.map((item) => <button type="button" key={item.snapshot.visitId} onClick={() => onSelect(item.snapshot.visitId)}><span>{item.snapshot.visitorLabel}</span><small>{new Date(item.snapshot.endedAt).toLocaleDateString('zh-CN')}　{item.snapshot.events.length} 条回应</small><b>↗</b></button>)}{archives.length === 0 && <p>还没有历史档案。</p>}</section>}
      {archive && <div className="archive-columns">
        <section className="archive-timeline"><p className="reading-label">留下的回应 / {events.length} 条 · 主动探索 {Math.round(archive.snapshot.activeDurationMs / 1000)} 秒</p>
          {events.length ? <ol>{events.map((event) => <li key={event.eventId}><span>{formatTime(event.elapsedMs)}</span><div><small>展区 {event.region} · {getExhibit(event.exhibitId)?.title ?? event.exhibitId}</small><strong>{ACTION_LABEL[event.action]}{event.action === 'comment' && event.detail ? `：${event.detail}` : ''}</strong></div></li>)}</ol> : <p className="archive-empty">这次参观没有留下回应。</p>}
          <div className="archive-text"><p className="reading-label">文字报告 · {archive.text.status === 'succeeded' ? '已完成' : archive.text.status === 'running' ? '生成中' : archive.text.status === 'failed' ? '未完成' : '本地模拟'}</p>{archive.text.result && <p>{archive.text.result}</p>}{archive.text.error && <p className="archive-error">{archive.text.error}</p>}{archive.text.status === 'failed' && <button className="plain-action" type="button" onClick={() => onRetry(archive.snapshot.visitId, 'text')}>重试文字报告 ↻</button>}</div>
        </section>
        <section className="archive-image"><p className="reading-label">示例复原图 · 原项目素材，非 AI 生成</p><img src="/assets/unity/art-14.png" alt="原项目示例复原图：原作14白翼" />{archive.image.status === 'running' && <p className="archive-status">图像任务处理中</p>}{archive.image.error && <p className="archive-error">{archive.image.error}</p>}{archive.image.status === 'failed' && <button className="plain-action" type="button" onClick={() => onRetry(archive.snapshot.visitId, 'image')}>重试图像归档 ↻</button>}</section>
      </div>}
    </div>
  </main>
}

export default function App() {
  const state = useVisitStore(useShallow(({ previewRemainingMs: _previewRemainingMs, ...visit }) => visit))
  const audio = useMuseumAudio(state.phase)
  const stageRef = useRef<HTMLElement>(null)
  useStageFit(stageRef)
  const [entryStep, setEntryStep] = useState<'home' | 'name'>('home')
  const [visitorDraft, setVisitorDraft] = useState('')
  const [showWelcomeHistory, setShowWelcomeHistory] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [localNotice, setLocalNotice] = useState('')
  const nameRef = useRef<HTMLInputElement>(null)
  const activeExhibit = useMemo(() => state.selectedExhibitId ? getExhibit(state.selectedExhibitId) ?? null : null, [state.selectedExhibitId])
  const selectedArchive = useMemo(() => state.selectedArchiveId ? state.archives.find((item) => item.snapshot.visitId === state.selectedArchiveId) ?? null : state.archives[state.archives.length - 1] ?? null, [state.archives, state.selectedArchiveId])
  const nearbyExhibit = state.nearbyExhibitId ? getExhibit(state.nearbyExhibitId) : null
  const sceneMounted = state.phase === 'exploring' || state.phase === 'paused'
  const sceneActive = state.phase === 'exploring' && !activeExhibit
  const notice = localNotice || state.notice

  useEffect(() => {
    if (!(state.phase === 'guide' || (state.phase === 'welcome' && entryStep === 'name'))) return
    // Start after the visitor expresses intent, without delaying the entry UI.
    void loadMuseumScene().then((module) => module.preloadMuseumAssets()).catch(failLoading)
  }, [entryStep, state.phase])

  useEffect(() => {
    let mounted = true
    void state.hydrate().catch(() => undefined).finally(() => { if (mounted) setIsHydrated(true) })
    return () => { mounted = false }
  }, [state.hydrate])
  useEffect(() => {
    if (state.phase === 'welcome' && entryStep === 'name') nameRef.current?.focus({ preventScroll: true })
  }, [entryStep, state.phase])
  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])
  useEffect(() => {
    const blur = () => state.setFocused(false)
    const focus = () => state.setFocused(true)
    const visibility = () => state.setFocused(!document.hidden)
    window.addEventListener('blur', blur); window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visibility)
    return () => { window.removeEventListener('blur', blur); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visibility) }
  }, [state.setFocused])
  useEffect(() => {
    if (state.phase !== 'exploring') return
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { if (activeExhibit) state.closeExhibit(); else state.pause(); return }
      if (activeExhibit || /input|textarea|select/i.test((event.target as HTMLElement)?.tagName ?? '')) return
      if (event.key.toLowerCase() === 'r') state.returnToEntrance()
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [activeExhibit, state.phase, state.closeExhibit, state.pause, state.returnToEntrance])

  const record = useCallback((action: BehaviorAction, detail?: string) => {
    const ok = state.record(action, detail)
    setLocalNotice(ok ? (action === 'comment' ? '留言已记录。' : `已记录：${ACTION_LABEL[action]}`) : '暂未记录；请在探索中重试。')
    window.setTimeout(() => setLocalNotice(''), 2200)
    return ok
  }, [state.record])
  const enterExhibit = useCallback((id: string) => { if (getExhibit(id)) state.openExhibit(id) }, [state.openExhibit])
  const startName = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); audio.activate(); if (!isHydrated) return; state.start(visitorDraft.trim().slice(0, 20) || '访客') }
  const exportArchive = (archive: ArchiveRecord) => {
    const objectUrl = URL.createObjectURL(new Blob([JSON.stringify(archive.snapshot, null, 2)], { type: 'application/json;charset=utf-8' }))
    const link = document.createElement('a'); link.href = objectUrl; link.download = `humanmuseum-${archive.snapshot.visitId.slice(0, 8)}.json`; link.click(); window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
  }
  const restart = () => { const label = visitorDraft.trim().slice(0, 20) || state.visitorLabel || '访客'; state.reset(); state.start(label) }
  const toggleFullscreen = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); else await stageRef.current?.requestFullscreen() } catch { /* Fullscreen is optional. */ }
  }
  const goEntryBack = () => setEntryStep('home')

  return <>
    {state.phase === 'welcome' && <main ref={stageRef} id="stage" className={`unity-stage unity-stage--${entryStep}`} data-view={entryStep}>
      <HomeHero />
      <Topbar onFullscreen={toggleFullscreen} fullscreen={isFullscreen} audio={audio} />
      {entryStep !== 'home' && <BackButton onClick={goEntryBack} />}
      {entryStep === 'home' && <><section className="entry-panel home-panel"><p className="era">21世纪</p><h1>人类<br />复原展</h1><button className="primary-button" type="button" onClick={() => { audio.activate(); setEntryStep('name') }}><span>开始参观</span><Arrow /></button></section><div className="home-utilities"><button className="home-history-button" type="button" onClick={() => setShowWelcomeHistory((v) => !v)}>历史档案　↗</button><p className="demo-note">示例模式 · 本地演示</p>
        {showWelcomeHistory && <div className="welcome-history">{state.archives.length === 0 ? <p>完成一次参观后，档案会保存在本机。</p> : state.archives.slice().reverse().map((archive) => <button type="button" key={archive.snapshot.visitId} onClick={() => state.selectArchive(archive.snapshot.visitId)}><span>{archive.snapshot.visitorLabel || '访客'}</span><small>{new Date(archive.snapshot.endedAt).toLocaleDateString('zh-CN')}</small>↗</button>)}</div>}
      </div></>}
      {entryStep === 'name' && <section className="entry-panel name-panel"><h1>如何称呼你？</h1><form onSubmit={startName}><label className="sr-only" htmlFor="visitor-name">名字或昵称</label><input ref={nameRef} id="visitor-name" value={visitorDraft} maxLength={20} placeholder="名字或昵称（可留空）" autoComplete="off" onChange={(event) => setVisitorDraft(event.target.value)} /><button className="primary-button" type="submit" disabled={!isHydrated}><span>继续</span><Arrow /></button></form><p className="step-hint">昵称仅用于这次参观档案</p></section>}
      <footer className="landing-footer"><span>可随时暂停</span><span>档案保存在本机</span></footer>
    </main>}

    {state.phase === 'guide' && <main ref={stageRef} id="stage" className="unity-stage intro-stage">
      <HomeHero /><Topbar onFullscreen={toggleFullscreen} fullscreen={isFullscreen} audio={audio} />
      <BackButton onClick={() => { state.reset(); setEntryStep('name') }} label="返回" />
      <section className="entry-panel intro-panel">
        <p className="visitor-label">{state.visitorLabel || '访客'}</p><h1>在 90 秒里，<br />留下你的回应。</h1>
        <p className="intro-copy">{state.autoEnterExhibits ? `靠近展品先看预览，停留 ${EXHIBIT_PREVIEW_MS / 1000} 秒自动进入。` : '靠近展品先看预览，按 E 或点击入口打开。'}</p>
        <div className="control-note">键盘 WASD 移动　+　鼠标环顾展厅<br />靠近展品按 E 打开　·　Esc 暂停</div>
        <div className="input-mode"><button type="button" aria-pressed={state.inputMode === 'pointer'} onClick={() => state.setInputMode('pointer')}>指针转向</button><button type="button" aria-pressed={state.inputMode === 'drag'} onClick={() => state.setInputMode('drag')}>拖动转向</button></div>
        <AutoEnterSetting enabled={state.autoEnterExhibits} onChange={state.setAutoEnterExhibits} />
        <button className="primary-button" type="button" onClick={() => { audio.activate(); state.begin() }}><span>进入展厅</span><Arrow /></button>
      </section>
    </main>}

    {sceneMounted && <main ref={stageRef} className="explore-stage">
      <div className="scene-stage"><SceneLoadBoundary><Suspense fallback={<MuseumLoading inputMode={state.inputMode} />}><MuseumScene active={sceneActive} inputMode={state.inputMode} resetToken={state.resetToken} onNearby={state.setNearby} onInteract={enterExhibit} onControlLost={state.pause} onTick={state.tick} /></Suspense></SceneLoadBoundary></div>
      <header className="explore-header"><span className="c430-wordmark">C430</span><span className="explore-remaining">{formatTime(state.remainingMs)}</span><SoundButton audio={audio} /><button type="button" className="explore-pause" onClick={() => state.pause()} aria-label="暂停参观">Ⅱ</button></header>
      {sceneActive && nearbyExhibit && <><ExhibitPreview key={nearbyExhibit.id} exhibit={nearbyExhibit} onCancel={state.cancelPreview} /><button className="scene-nearby" type="button" onClick={() => enterExhibit(nearbyExhibit.id)}><span>E</span> {nearbyExhibit.title} · 立即打开</button></>}
      <div className="scene-crosshair" aria-hidden="true" />
      <footer className="explore-footer"><span>01 影像　/　02 留言　/　04 态度　/　05 传播</span><span>{state.events.length.toString().padStart(2, '0')} 条回应</span><button type="button" onClick={() => state.finish('visitor_finished')}>结束参观 ↗</button></footer>
      {notice && <div className="explore-notice" role="status">{notice}</div>}
      {state.persistenceStatus === 'error' && <div className="explore-notice explore-notice--error" role="alert">本机记录尚未保存，请在结束后导出档案。</div>}
      {state.phase === 'paused' && <div className="pause-overlay"><section>
        <div className="pause-topline"><p className="reading-label">时间已暂停</p><SoundButton audio={audio} /></div>
        <h1>暂歇。</h1><p>准备好后，由你决定何时继续。</p>
        <AutoEnterSetting enabled={state.autoEnterExhibits} onChange={state.setAutoEnterExhibits} />
        <button className="primary-button" disabled={!state.isFocused} onClick={() => { audio.activate(); state.resume() }}><span>继续参观</span><Arrow /></button>
        <button className="line-action" onClick={() => state.finish('visitor_finished')}>结束本轮 →</button>
      </section></div>}
      {activeExhibit && <ExhibitDialog key={activeExhibit.id} exhibit={activeExhibit} onClose={state.closeExhibit} onRecord={record} recordedActions={state.events.filter((event) => event.exhibitId === activeExhibit.id).map((event) => event.action)} audio={audio} />}
    </main>}

    {state.phase === 'archive' && <ArchiveView archive={selectedArchive} archives={state.archives} onSelect={state.selectArchive} onRetry={state.retryGeneration} onNewVisit={restart} onEntrance={() => { state.returnToEntrance(); setEntryStep('home') }} persistenceStatus={state.persistenceStatus} onExport={exportArchive} audio={audio} />}
  </>
}
